import {
	createLocalAudioTrack,
	createLocalTracks,
	createLocalVideoTrack,
	LocalAudioTrack,
	LocalVideoTrack,
} from "twilio-video";
import * as React from "react";
import { makeLogger } from "../../utils/logger";
import { useSnackbar } from "notistack";
import { getIntl } from "../../localization/intl";

const log = makeLogger("Twilio - Local tracks");

const MEDIA_ERROR_TOAST_KEY = "MEDIA_ERROR_TOAST";

export function useLocalTracks() {
	const [audioTrack, setAudioTrack] = React.useState<LocalAudioTrack>();
	const [videoTrack, setVideoTrack] = React.useState<LocalVideoTrack>();
	const [isAcquiringLocalTracks, setIsAcquiringLocalTracks] = React.useState<boolean>();
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();

	const getLocalAudioTrack = React.useCallback(async () => {
		log.debug("get audio track");
		const track = await createLocalAudioTrack();
		setAudioTrack(track);
		return track;
	}, []);

	const getLocalVideoTrack = React.useCallback(async () => {
		log.debug("get video track");
		const track = await createLocalVideoTrack({
			name: `camera-${Date.now()}`,
		});
		setVideoTrack(track);
		return track;
	}, []);

	const removeLocalAudioTrack = React.useCallback(() => {
		log.debug("remove audio track");
		if (audioTrack) {
			audioTrack.stop();
			setAudioTrack(undefined);
		}
	}, [audioTrack]);

	const removeLocalVideoTrack = React.useCallback(() => {
		log.debug("remove video track");
		if (videoTrack) {
			videoTrack.stop();
			setVideoTrack(undefined);
		}
	}, [videoTrack]);

	const getAudioAndVideoTracks = React.useCallback(async () => {
		log.debug("get audio and video tracks");
		setIsAcquiringLocalTracks(true);
		try {
			const tracks = await createLocalTracks({
				video: {
					name: `camera-${Date.now()}`,
				},
				audio: {},
			});
			const newVideoTrack = tracks.find((track) => track.kind === "video");
			const newAudioTrack = tracks.find((track) => track.kind === "audio");
			if (newVideoTrack) {
				setVideoTrack(newVideoTrack as LocalVideoTrack);
			}
			if (newAudioTrack) {
				setAudioTrack(newAudioTrack as LocalAudioTrack);
			}

			return tracks;
		} catch (err) {
			enqueueSnackbar(getIntl().formatMessage({ id: `TWILIO.error.${err.name.toLowerCase()}` }), {
				variant: "error",
				persist: true,
				key: MEDIA_ERROR_TOAST_KEY
			});
			console.error(err);
		} finally {
			setIsAcquiringLocalTracks(false);
		}
	}, [enqueueSnackbar]);

	const localTracks = React.useMemo(
		() => [audioTrack, videoTrack].filter(Boolean) as (LocalAudioTrack | LocalVideoTrack)[],
		[audioTrack, videoTrack],
	);

	React.useEffect(() => {
		return () => {
			closeSnackbar(MEDIA_ERROR_TOAST_KEY);
		}
	}, [closeSnackbar]);

	return {
		localTracks,
		getAudioAndVideoTracks,
		isAcquiringLocalTracks,
		getLocalAudioTrack,
		getLocalVideoTrack,
		removeLocalAudioTrack,
		removeLocalVideoTrack,
	};
}
